/**
 * Public /enrol/company landing — same funnel pattern as sole-trader, tuned
 * for the 2-owner Xero company ICP.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import Brand from "@/components/Brand";
import EnrolWizard from "@/components/EnrolWizard";

const PLAN = { label: "2-Owner Company Tax Compliance", base_nzd: 249 };

export default function EnrolCompany() {
    useEffect(() => {
        const prev = document.title;
        document.title = "2-Owner Company Tax — $249+GST/mo · NZ Taxation Limited";
        return () => { document.title = prev; };
    }, []);

    return (
        <div className="min-h-screen bg-[#F5F4EF]">
            <header className="mx-auto max-w-5xl px-4 py-6 flex items-center justify-between">
                <Brand size="sm" data-testid="enrol-brand-home" />
                <Link to="/login" className="text-[13px] text-[#5E636E] hover:text-[#0F1115]" data-testid="enrol-signin-link">Sign in</Link>
            </header>

            <main className="mx-auto max-w-5xl px-4 pb-16">
                <section className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-[#0F1115] text-white px-3 py-1 text-[11px] uppercase tracking-wider">
                            <span>NZ Taxation Limited t/a ClientOps</span>
                            <span className="opacity-60">·</span>
                            <span>2-owner company on Xero</span>
                        </div>
                        <h1 className="mt-4 text-[32px] md:text-[42px] leading-tight font-semibold text-[#0F1115]">
                            Your company's tax, done for you.<br />
                            <span className="text-[#0F1115]">NZ$249 + GST</span> a month.
                        </h1>
                        <p className="mt-3 text-[15px] leading-6 text-[#1F2328]">
                            Company IR4, financial statements, both shareholder IR3s, and GST returns — all handled monthly for one predictable fee. Built for two-owner companies already running on Xero.
                        </p>

                        <ul className="mt-6 space-y-3 text-[14px] text-[#1F2328]">
                            <li className="flex items-start gap-3" data-testid="enrol-co-benefit-1">
                                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#0F1115] text-white text-[12px] leading-5 text-center">✓</span>
                                <span><b>Annual IR4 + financial statements</b> — filed to IRD minimum standards.</span>
                            </li>
                            <li className="flex items-start gap-3" data-testid="enrol-co-benefit-2">
                                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#0F1115] text-white text-[12px] leading-5 text-center">✓</span>
                                <span><b>Both shareholders' IR3s</b> — no separate personal invoices, one flat fee.</span>
                            </li>
                            <li className="flex items-start gap-3" data-testid="enrol-co-benefit-3">
                                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#0F1115] text-white text-[12px] leading-5 text-center">✓</span>
                                <span><b>GST returns handled from your Xero file</b> — invite us as your Adviser, we take it from there.</span>
                            </li>
                        </ul>

                        <div className="mt-8 text-[12px] text-[#5E636E]">
                            Signed up in under 10 minutes. A NZ Taxation team member calls to welcome you before your first invoice. Cancellable in writing with 30 days notice.
                        </div>
                    </div>

                    <div id="enrol">
                        <EnrolWizard service="company_2owner" plan={PLAN} />
                    </div>
                </section>

                <section className="mt-14 border-t border-[#E4E4E7] pt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-[13px] text-[#1F2328]">
                    <div>
                        <div className="font-medium text-[#0F1115] mb-2">What's included</div>
                        <ul className="space-y-1 text-[13px]">
                            <li>· IR4 company income tax return (annual)</li>
                            <li>· Financial statements to IRD minimum</li>
                            <li>· Both shareholders' IR3 returns</li>
                            <li>· GST returns (while registered)</li>
                            <li>· IRD correspondence as your agent</li>
                        </ul>
                    </div>
                    <div>
                        <div className="font-medium text-[#0F1115] mb-2">What's not included</div>
                        <ul className="space-y-1 text-[13px]">
                            <li>· Xero coding / month-end bookkeeping</li>
                            <li>· Payroll processing</li>
                            <li>· Business advisory & forecasting</li>
                            <li className="text-[#5E636E]">(Ask us — we can add these on)</li>
                        </ul>
                    </div>
                    <div>
                        <div className="font-medium text-[#0F1115] mb-2">How it works</div>
                        <ol className="space-y-1 text-[13px] list-decimal ml-4">
                            <li>Fill in the wizard →</li>
                            <li>Sign the Authority to Act (typed name)</li>
                            <li>We call to welcome you within 24 hours</li>
                            <li>Invite us to your Xero — first invoice follows</li>
                        </ol>
                    </div>
                </section>
            </main>

            <footer className="border-t border-[#E4E4E7] bg-white">
                <div className="mx-auto max-w-5xl px-4 py-6 text-[12px] text-[#5E636E] flex flex-wrap gap-x-4 gap-y-2 justify-between">
                    <span>© NZ Taxation Limited t/a ClientOps</span>
                    <Link to="/privacy" className="hover:text-[#0F1115]">Privacy</Link>
                    <Link to="/support" className="hover:text-[#0F1115]">Support</Link>
                </div>
            </footer>
        </div>
    );
}
